// ============================================================
// TATHYA DMS - Force Change Password Screen
// Version: 1.0.0
// Date: 2026-04-18
// ============================================================

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { changePassword } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const ChangePassword = () => {
  const { org_slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const isForced = user?.password_reset_required;

  // ── Live password-rule checklist (Apr 24) ──
  // Each rule evaluated on every keystroke. Turns green + ✓ when
  // met, stays red + ✗ until then. Submit is disabled until ALL
  // rules pass AND the confirm field matches. This matches the
  // user's expectation of seeing progress as they type rather
  // than a generic "password invalid" error only on submit.
  const rules = [
    { key: 'len',     label: 'At least 8 characters',          test: (p) => p.length >= 8 },
    { key: 'upper',   label: 'At least one uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
    { key: 'lower',   label: 'At least one lowercase letter (a-z)', test: (p) => /[a-z]/.test(p) },
    { key: 'digit',   label: 'At least one number (0-9)',      test: (p) => /[0-9]/.test(p) },
    { key: 'special', label: 'At least one special character (!@#$%^&*...)',
                      test: (p) => /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`';]/.test(p) }
  ];

  const newPwd = form.new_password;
  const confirmPwd = form.confirm_password;
  const ruleResults = rules.map(r => ({ ...r, passed: r.test(newPwd) }));
  const allRulesPass = ruleResults.every(r => r.passed);
  const passwordsMatch = newPwd.length > 0 && newPwd === confirmPwd;
  const canSubmit = allRulesPass && passwordsMatch &&
                    (isForced || form.current_password.length > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Defence in depth — UI should have gated this already, but
    // guard anyway so a broken disabled-attr doesn't let invalid
    // data through.
    if (!allRulesPass) {
      return setError('Please satisfy all password rules below.');
    }
    if (!passwordsMatch) {
      return setError('New passwords do not match.');
    }

    setLoading(true);
    try {
      await changePassword({
        current_password: form.current_password,
        new_password: form.new_password
      });

      // Update local user state
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.password_reset_required = false;
      localStorage.setItem('user', JSON.stringify(stored));

      if (isForced) {
        navigate(`/${org_slug}/dashboard`);
      } else {
        // Self-service: show success inline, reset form, stay on page.
        setSuccess('Password changed successfully.');
        setForm({ current_password: '', new_password: '', confirm_password: '' });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={isForced ? styles.container : styles.pageContainer}>
      <div style={isForced ? styles.card : styles.panel}>
        <div style={styles.header}>
          {isForced && <div style={styles.logo}>T</div>}
          <h1 style={styles.title}>
            {isForced ? 'Set Your Password' : 'Change Password'}
          </h1>
          {isForced && (
            <p style={styles.notice}>
              You must set a new password before continuing.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {!isForced && (
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={form.current_password}
                onChange={e => setForm({...form, current_password: e.target.value})}
                placeholder="Enter current password"
              />
            </div>
          )}

          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={form.new_password}
              onChange={e => setForm({...form, new_password: e.target.value})}
              placeholder="Enter a new password"
              required
              autoFocus
            />
          </div>

          {/* ── Live rule checklist (Apr 24) ──
              Each rule turns green as the user types a password
              that satisfies it. Stays visible at all times so the
              user knows what's expected before they start typing. */}
          <ul style={styles.rulesList}>
            {ruleResults.map(r => (
              <li
                key={r.key}
                style={{
                  ...styles.ruleItem,
                  color: r.passed ? '#1E7B34' : '#C0392B'
                }}
              >
                <span style={styles.ruleIcon}>{r.passed ? '✓' : '✗'}</span>
                {r.label}
              </li>
            ))}
          </ul>

          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={form.confirm_password}
              onChange={e => setForm({...form, confirm_password: e.target.value})}
              placeholder="Repeat new password"
              required
            />
          </div>

          {/* Match indicator — only shown once the user has started
              typing in the confirm field, to avoid a scary red "do
              not match" message before they've had a chance. */}
          {confirmPwd.length > 0 && (
            <p style={{
              ...styles.matchIndicator,
              color: passwordsMatch ? '#1E7B34' : '#C0392B'
            }}>
              <span style={styles.ruleIcon}>{passwordsMatch ? '✓' : '✗'}</span>
              {passwordsMatch ? 'Passwords match' : 'Passwords do not match yet'}
            </p>
          )}

          {error && <p className="error-msg">{error}</p>}
          {success && <p style={styles.successMsg}>{success}</p>}

          <button
            type="submit"
            className="btn btn-primary"
            style={styles.submitBtn}
            disabled={loading || !canSubmit}
          >
            {loading ? 'Saving...' : (isForced ? 'Set New Password' : 'Change Password')}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1B3A5C 0%, #2E75B6 100%)',
    padding: '20px'
  },
  // Self-service variant — rendered inside Layout (sidebar visible).
  // No full-screen gradient; a plain card on the normal page
  // background, aligned to the top-left of the content area.
  pageContainer: {
    padding: '0'
  },
  panel: {
    background: 'white',
    borderRadius: '10px',
    padding: '28px 32px',
    maxWidth: '480px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  logo: {
    width: '60px',
    height: '60px',
    background: 'linear-gradient(135deg, #1B3A5C, #2E75B6)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    fontWeight: '700',
    color: 'white',
    margin: '0 auto 12px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1B3A5C'
  },
  notice: {
    color: '#B7600A',
    fontSize: '13px',
    marginTop: '8px',
    background: '#FFF2CC',
    padding: '8px 12px',
    borderRadius: '6px'
  },
  submitBtn: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    marginTop: '8px'
  },
  // ── Rule checklist (Apr 24) ──
  rulesList: {
    listStyle: 'none',
    padding: '10px 12px',
    margin: '0 0 14px 0',
    background: '#FAFBFC',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '12px'
  },
  ruleItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '3px 0',
    transition: 'color 120ms'
  },
  ruleIcon: {
    fontWeight: 'bold',
    width: '14px',
    textAlign: 'center'
  },
  matchIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    margin: '-6px 0 14px 0',
    transition: 'color 120ms'
  },
  successMsg: {
    color: '#1E7B34',
    background: '#E2F0D9',
    border: '1px solid #b8dba8',
    borderRadius: '6px',
    padding: '10px 14px',
    fontSize: '13px',
    marginBottom: '12px'
  }
};

export default ChangePassword;
